import httpService from '@/utils/httpService';
import store from '@/store';
import { CONDITION_RATING_ORDER } from './cmlColoringConstants';

/**
 * Slim read wrappers for the multi-level condition coloring feature (AVA-348).
 *
 * Two endpoints are served:
 *   GET /api/v1/masters/coloring  → MasterColoringDto[]  (worst-of cached metrics per master tag)
 *   GET /api/v1/cmls/coloring     → CmlColoringDto[]     (slim geometry + worst-of per CML)
 *
 * Both endpoints are facility-scoped (X-Facility-Id required) and gated behind
 * MastersView OR InspectionTags.View permission.
 *
 * httpService.get takes a { url, headers } config object (NOT a bare URL string).
 * Callers await and receive response.data (the raw axios response is unwrapped here).
 */

/**
 * X-Facility-Id header when a facility/asset is active (store.state.assetId = the facility).
 * Defensively omits the header if assetId is absent so the API is never handed an
 * empty facility value. Both coloring endpoints require facility scoping.
 * @returns {{ 'X-Facility-Id'?: string }}
 */
function facilityHeaders() {
  const assetId = store.state.assetId;
  return assetId ? { 'X-Facility-Id': assetId } : {};
}

// conditionRating boundary shim — the server emits this inconsistently (integer on
// some paths per TRK-B-1; numeric string from the coloring projection's enum .ToString()).
// Normalize any of {integer, numeric-string, enum-name, null} → canonical enum name | null,
// so downstream (useLevelColoring) can assume the name. Mirror of useMonitoringTab.js's shim.
function normalizeConditionRating(v) {
  if (v == null) return null;
  if (typeof v === 'number') return CONDITION_RATING_ORDER[v] ?? null;
  if (CONDITION_RATING_ORDER.includes(v)) return v;          // already the enum name
  const n = Number(v);                                       // numeric string e.g. "1"
  return Number.isInteger(n) ? (CONDITION_RATING_ORDER[n] ?? null) : null;
}

/**
 * Build repeated `tagTypes=` query params from a string array.
 * Returns an empty string when tagTypes is empty/absent (no params appended).
 * @param {string[]} tagTypes
 * @returns {string}
 */
const typesQuery = (tagTypes) =>
  (tagTypes || []).map((t) => `tagTypes=${encodeURIComponent(t)}`).join('&');

/**
 * Master-level coloring rows — worst-of cached metrics + the master's OWN Forge element
 * dbIds (ForgeBaseTag.Children) per master tag. The dbIds drive master-level 3D paint and
 * the CML-level grey ghost backdrop (no AMIS inspection harvest needed).
 * @param {string[]} tagTypes - discriminator strings, e.g. ['ForgeEquipment', 'ForgePipeline']
 * @param {string} [masterTagId] - when set, scopes to a single master (CML-level ghost fetch)
 * @returns {Promise<Array<{ id: string, name: string, worstCR: number|null, minRL: number|null, currentMinWT: number|null, conditionRating: string|null, dbIds: number[] }>>}
 *   conditionRating is the canonical enum name (or null) after boundary normalization.
 */
export async function getMasterColoring(tagTypes, masterTagId) {
  const parts = [typesQuery(tagTypes)];
  if (masterTagId) parts.push(`masterTagId=${encodeURIComponent(masterTagId)}`);
  const query = parts.filter(Boolean).join('&');
  const response = await httpService.get({
    url: `/api/v1/masters/coloring${query ? `?${query}` : ''}`,
    headers: facilityHeaders(),
  });
  const rows = response.data;
  return (rows || []).map(r => ({ ...r, conditionRating: normalizeConditionRating(r.conditionRating) }));
}

/**
 * CML-level coloring rows — slim geometry + worst-of metrics per CML record.
 * Consumed by T13 (CML-level 3D paint pass). Supports optional scoping by
 * masterTagId and includeArchived flag.
 * @param {{ tagTypes?: string[], masterTagId?: string, includeArchived?: boolean }} params
 * @returns {Promise<Array<{ id: string, masterTagId: string, cmlNo: string, worstCR: number|null, minRL: number|null, currentMinWT: number|null, conditionRating: string|null, point: object|null, forgeElementDbId: number|null, isArchived: boolean }>>}
 *   conditionRating is the canonical enum name (or null) after boundary normalization.
 */
export async function getCmlColoring({ tagTypes, masterTagId, includeArchived = false } = {}) {
  const parts = [typesQuery(tagTypes)];
  if (masterTagId) parts.push(`masterTagId=${encodeURIComponent(masterTagId)}`);
  if (includeArchived) parts.push('includeArchived=true');
  const query = parts.filter(Boolean).join('&');
  const response = await httpService.get({
    url: `/api/v1/cmls/coloring?${query}`,
    headers: facilityHeaders(),
  });
  const rows = response.data;
  return (rows || []).map(r => ({ ...r, conditionRating: normalizeConditionRating(r.conditionRating) }));
}
